import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, MenuItem, Stack, Typography } from '@emburse/embark-core';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import ForteApplicationFormRow from '../Common/ForteApplicationFormRow';
import { ForteApplicationContext } from '../Common/ForteApplicationProvider';
import { AccountType } from '../../../api/forteApplication';
import { $t, useTranslation } from '../../../hooks/useTranslation';
import { FormSelect, FormTextField } from '../../../components/FormComponents';
const maskedPattern = /^\*+\d{4}$/; // Pattern for masked routing number
function BankAccountInfo({ saveButtonText, afterFormSave, backButtonText, backButtonCallback }) {
    useTranslation();
    const { currentApplication, saveApplication, error, loading } = useContext(ForteApplicationContext);
    const baseFormSchema = useMemo(() => z.object({
        routingNumber: z
            .string()
            .refine((val) => /^\d{9}$/.test(val) ||
            (maskedPattern.test(val) &&
                val ===
                    currentApplication?.applicant_organization.bank_routing_number), { message: 'Routing Number must be a 9-digit number' }),
        accountType: z.nativeEnum(AccountType, { message: 'Invalid account type' })
    }), [currentApplication?.applicant_organization.bank_routing_number]);
    const accountNumberFormSchema = useMemo(() => z
        .object({
        accountNumber: z
            .string()
            .refine((val) => /^\d{4,17}$/.test(val) ||
            (maskedPattern.test(val) &&
                val ===
                    currentApplication?.applicant_organization
                        .bank_account_number), { message: 'Account Number must be between 4 and 17 digits long' }),
        confirmAccountNumber: z.string()
    })
        .refine((data) => data.accountNumber === data.confirmAccountNumber, {
        message: 'Account numbers do not match',
        path: ['confirmAccountNumber']
    }), [currentApplication?.applicant_organization.bank_account_number]);
    const formSchema = z.intersection(baseFormSchema, accountNumberFormSchema);
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            routingNumber: '',
            accountNumber: '',
            confirmAccountNumber: '',
            accountType: AccountType.Checking
        }
    });
    useEffect(() => {
        if (!loading && !error && currentApplication) {
            const formData = {
                routingNumber: currentApplication.applicant_organization.bank_routing_number || '',
                accountNumber: currentApplication.applicant_organization.bank_account_number || '',
                confirmAccountNumber: currentApplication.applicant_organization.bank_account_number || '',
                accountType: currentApplication.applicant_organization.bank_account_type ||
                    AccountType.Checking
            };
            form.reset(formData);
        }
    }, [loading, currentApplication, form, error]);
    const submitForm = useCallback(async (data) => {
        if (currentApplication) {
            const initialFormData = {
                routingNumber: currentApplication.applicant_organization.bank_routing_number || '',
                accountNumber: currentApplication.applicant_organization.bank_account_number || '',
                confirmAccountNumber: currentApplication.applicant_organization.bank_account_number || '',
                accountType: currentApplication.applicant_organization.bank_account_type ||
                    AccountType.Checking
            };
            const updatedApplication = {
                ...currentApplication,
                applicant_organization: {
                    ...currentApplication.applicant_organization,
                    bank_account_type: data.accountType
                }
            };
            if (data.routingNumber !== initialFormData.routingNumber &&
                !maskedPattern.test(data.routingNumber)) {
                updatedApplication.applicant_organization.bank_routing_number =
                    data.routingNumber;
            }
            if (data.accountNumber !== initialFormData.accountNumber &&
                !maskedPattern.test(data.accountNumber)) {
                updatedApplication.applicant_organization.bank_account_number =
                    data.accountNumber;
            }
            await saveApplication({
                ...currentApplication,
                applicant_organization: {
                    ...currentApplication.applicant_organization,
                    bank_routing_number: data.routingNumber,
                    bank_account_number: data.accountNumber,
                    bank_account_type: data.accountType
                }
            });
            afterFormSave?.();
        }
    }, [afterFormSave, currentApplication, saveApplication]);
    return (_jsx(FormProvider, { ...form, children: _jsx("form", { children: _jsxs(Stack, { alignItems: "flex-start", sx: { width: '100%' }, spacing: 3, children: [_jsxs(Stack, { direction: "column", spacing: 0, alignItems: "flex-start", sx: { width: '100%' }, children: [_jsx(Typography, { variant: "h6", sx: { flexGrow: 1 }, children: $t('Bank Account Information') }), _jsx(Typography, { variant: "body2", color: "textSecondary", children: $t(`Enter your organization’s bank account details. This will be the account from which funds are withdrawn to reimburse your employees.`) })] }), _jsx(ForteApplicationFormRow, { bottomSpacer: !form.formState.errors.accountType, children: _jsxs(FormSelect, { id: "accountType", name: "accountType", helperText: form.formState.errors.accountType?.message, label: $t('Account Type'), fullWidth: true, children: [_jsx(MenuItem, { value: AccountType.Checking, children: "Checking" }), _jsx(MenuItem, { value: AccountType.Savings, children: "Savings" })] }) }), _jsx(ForteApplicationFormRow, { bottomSpacer: !form.formState.errors.routingNumber, children: _jsx(FormTextField, { id: "routingNumber", name: "routingNumber", label: $t('Routing Number'), helperText: form.formState.errors.routingNumber?.message, fullWidth: true }) }), _jsx(ForteApplicationFormRow, { bottomSpacer: !form.formState.errors.accountNumber, children: _jsx(FormTextField, { id: "accountNumber", name: "accountNumber", label: $t('Account Number'), helperText: form.formState.errors.accountNumber?.message, onChange: async () => {
                                await form.trigger('accountNumber');
                                await form.trigger('confirmAccountNumber');
                            }, fullWidth: true }) }), _jsx(ForteApplicationFormRow, { bottomSpacer: !form.formState.errors.confirmAccountNumber, children: _jsx(FormTextField, { id: "confirmAccountNumber", name: "confirmAccountNumber", label: $t('Confirm Account Number'), helperText: form.formState.errors.confirmAccountNumber?.message, fullWidth: true }) }), _jsxs(Stack, { direction: "row", justifyContent: "space-between", sx: { width: '100%' }, children: [backButtonCallback !== undefined && (_jsx(Button, { variant: "outlined", onClick: backButtonCallback, children: backButtonText || $t('Back') })), _jsx(Button, { onClick: form.handleSubmit(submitForm), children: saveButtonText || $t('Next') })] })] }) }) }));
}
BankAccountInfo.defaultProps = {
    saveButtonText: 'Next',
    backButtonText: 'Back',
    afterFormSave: undefined,
    backButtonCallback: undefined
};
export default BankAccountInfo;
